import { faComment, faShare, faThumbsUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef, useState } from "react";
import { Tooltip, OverlayTrigger } from "react-bootstrap";
import reactionLike from "../../../assets/reactions/like.png";
import reactionLove from "../../../assets/reactions/love.png";
import reactionHaha from "../../../assets/reactions/haha.png";
import reactionBlessings from "../../../assets/reactions/blessings.png";
import reactionWow from "../../../assets/reactions/wow.png";
import reactionSad from "../../../assets/reactions/sad.png";
import reactionAngry from "../../../assets/reactions/angry.png";
// Component to show post reacts like, love, haha, wow, sad, angry
// Each reaction is a full color image from assets/reactions/ folder

const reaction_map = {
  Like: {
    url: reactionLike,
    id: 1,
    type: "like",
    caption: "Like",
  },
  Love: {
    url: reactionLove,
    id: 2,
    type: "love",
    caption: "Love",
  },
  Haha: {
    url: reactionHaha,
    id: 4,
    type: "haha",
    caption: "Haha",
  },
  Blessings: {
    url: reactionBlessings,
    id: 7,
    type: "blessings",
    caption: "Blessings",
  },
  Wow: {
    url: reactionWow,
    id: 3,
    type: "wow",
    caption: "Wow",
  },
  Sad: {
    url: reactionSad,
    id: 5,
    type: "sad",
    caption: "Sad",
  },
  Angry: {
    url: reactionAngry,
    id: 6,
    type: "angry",
    caption: "Angry",
  },
};

const Reaction = ({ reaction, onClick, key }) => {
  const triggerRef = useRef(null);
  const [container, setContainer] = useState(null);

  useEffect(() => {
    if (triggerRef.current) {
      const owner = triggerRef.current.closest(".post-item-footer");
      setContainer(owner ?? triggerRef.current.ownerDocument?.body ?? null);
    }
  }, []);

  return (
    <OverlayTrigger
      placement="top"
      trigger={["hover", "focus"]}
      delay={{ show: 120, hide: 0 }}
      container={container || undefined}
      popperConfig={{
        strategy: "fixed",
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, 8],
            },
          },
        ],
      }}
      overlay={(props) => (
        <Tooltip
          id={`tooltip-${reaction.type}`}
          {...props}
          className="post-reaction-tooltip"
          arrowProps={{ style: { display: "none" } }}
        >
          {reaction.type}
        </Tooltip>
      )}
    >
      <span ref={triggerRef} className="post-reaction" onClick={(e) => {e.stopPropagation(); onClick(reaction)}}>
        <img src={reaction.url} alt={reaction.type} />
      </span>
    </OverlayTrigger>
  );
};

const PostReactions = ({onClick}) => {
  const stop = (e) => e.stopPropagation();

  return (
    <div className="post-reactions"
      onClick={stop}
      onMouseDown={stop}
      onPointerDown={stop}
    >
      {Object.keys(reaction_map).map((key) => (
        <Reaction key={key} reaction={reaction_map[key]} onClick={onClick} />
      ))}
    </div>
  )
}



const ReactionSummaryItem = ({ reaction }) => {
  const triggerRef = useRef(null);
  const [container, setContainer] = useState(null);

  useEffect(() => {
    if (triggerRef.current) {
      const owner = triggerRef.current.closest(".post-item-footer");
      setContainer(owner ?? triggerRef.current.ownerDocument?.body ?? null);
    }
  }, []);

  const iconUrl = reaction_map[reaction.caption].url;
  if (!iconUrl) {
    return null;
  }

  const count = reaction.reaction_count ?? 0;
  const caption = reaction.caption ?? "reaction";
  const label = `${count}`;

  return (
    <OverlayTrigger
      placement="top"
      trigger={["hover", "focus"]}
      delay={{ show: 120, hide: 0 }}
      container={container || undefined}
      popperConfig={{
        strategy: "fixed",
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, 0],
            },
          },
        ],
      }}
      overlay={(props) => (
        <Tooltip
          id={`tooltip-summary-${reaction.caption}`}
          {...props}
          className="post-reaction-tooltip"
          arrowProps={{ style: { display: "none" } }}
        >
          {label}
        </Tooltip>
      )}
    >
      <span ref={triggerRef} className="post-reaction-summary-item">
        <img src={iconUrl} alt={reaction.caption} />
      </span>
    </OverlayTrigger>
  );
};

const PostReactionSummary = ({reactions}) => {
  //Convert reactions to array
  const reactionArray = Object.values(reactions); 

  if(reactionArray.length === 0) return null;

  const totalReactions = reactionArray.reduce((sum, reaction) => sum + (reaction.reaction_count || 0), 0);

  return(
    <div className="post-reaction-summary">
      {reactionArray.map((reaction) => (
        <ReactionSummaryItem key={`reaction-${reaction.caption}`} reaction={reaction} />
      ))}
      { totalReactions > 0 && <span>{totalReactions} {totalReactions === 1 ? "person" : "people"} reacted to this</span> }
    </div>
  )
};

export const PostFooter = ({
  is_like,
  action_id,
  handles,
  handleCommentToggle,
  postData,
}) => {
  const [isLike, setLike] = useState(is_like ? true : false);

  const { handleLike, handleUnlike } = handles;

  const handleReact = (reaction) => {
    handleLike(action_id, reaction); 
    setLike(true); 
  }

  return (
    <div className="row post-item-footer">
      <PostReactionSummary reactions={postData.feed_reactions ?? {}} />
      <div
        className={isLike ? "post-item-footer-like color-blue" : "post-item-footer-like"}
        onClick={
          isLike
            ? () => {
                handleUnlike(action_id);
                setLike(false);
              }
            : () => {
                handleLike(action_id);
                setLike(true);
              }
        }
      >
        <FontAwesomeIcon icon={faThumbsUp} /> Like
        <PostReactions onClick={handleReact}/>
      </div>
      {postData.can_comment === 1 && (
        <div onClick={handleCommentToggle}>
          <FontAwesomeIcon icon={faComment} /> Comment
        </div>
      )}
      {postData.can_share === 1 && (
        <div
          onClick={() => {
            handles.handleShowShare(postData);
          }}
          className=""
        >
          <FontAwesomeIcon icon={faShare} /> Share
        </div>
      )}
    </div>
  );
};